// Date helpers — kept dep-free (no dayjs / date-fns). Only operations needed
// by the orders feature: "today as ISO LocalDate" for the default filter and
// "format ISO LocalDate as `D. M. YYYY` (Czech short format)" for display.

const CS_DATE_LOCALE = 'cs-CZ';
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Returns today's date as ISO LocalDate (`YYYY-MM-DD`) in the browser's
// local timezone. Used as the default `from` filter (UI-01.2 — "default =
// today").
export const todayIsoDate = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isoFromParts = (y: number, m0: number, d: number): string => {
  const mm = String(m0 + 1).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
};

// First day of the current month as ISO LocalDate (`YYYY-MM-01`), in the
// browser's local timezone. The export date-range "Od" default (CHR-02 §7).
export const firstOfMonthIsoDate = (): string => {
  const now = new Date();
  return isoFromParts(now.getFullYear(), now.getMonth(), 1);
};

// Last day of the current month as ISO LocalDate, in the browser's local
// timezone (day 0 of next month = last day of this month). The export
// date-range "Do" default (CHR-02 §7).
export const lastOfMonthIsoDate = (): string => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return isoFromParts(now.getFullYear(), now.getMonth(), lastDay);
};

// Format an ISO LocalDate (`YYYY-MM-DD`) as Czech short form (`26. 5. 2026`).
// Returns empty string for null/blank/invalid input.
export const formatDateCs = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const match = ISO_DATE_RE.exec(iso);
  if (!match) return '';
  // Construct a Date with a noon-local time to dodge any DST edge that could
  // shift the wall date back / forward when the browser timezone is exotic.
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const dt = new Date(y, mo - 1, d, 12, 0, 0);
  return dt.toLocaleDateString(CS_DATE_LOCALE, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
};

// Fixed, deterministic Czech weekday abbreviations indexed by `Date#getDay()`
// (0 = Sunday … 6 = Saturday). Hard-coded rather than derived from
// `toLocaleDateString('cs-CZ', { weekday: 'short' })`, which yields lowercase /
// locale-variant output ("po", "út", …) that drifts across ICU builds. CHR-05
// requires the capitalized `Po/Út/St/Čt/Pá/So/Ne` form.
const CS_WEEKDAY_ABBR = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'] as const;

// Format an ISO LocalDate (`YYYY-MM-DD`) as the capitalized Czech weekday
// abbreviation (`Po…Ne`) — CHR-05 "DEN" column, derived read-only from
// `loadingDate`. Returns empty string for null/blank/invalid input (same guard
// as `formatDateCs`). Uses the same noon-local DST-dodge construction.
export const formatWeekdayCs = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const match = ISO_DATE_RE.exec(iso);
  if (!match) return '';
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const dt = new Date(y, mo - 1, d, 12, 0, 0);
  // getDay() is always 0–6, but `?? ''` satisfies noUncheckedIndexedAccess
  // (the production build tsconfig) and keeps the empty-string contract.
  return CS_WEEKDAY_ABBR[dt.getDay()] ?? '';
};

// Format an ISO Instant (e.g. `2026-05-28T08:30:15Z`) as Czech date + time
// (`28. 5. 2026 8:30`) in the browser's local timezone. Used by the admin
// notification audit "KDY" column (CHR-01 / ADR-0034). Returns empty string
// for null/blank/unparseable input.
export const formatDateTimeCs = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(CS_DATE_LOCALE, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Format a `HH:mm` time-of-day string for display. Returns empty string for
// null/blank input. Cs and en formats coincide here (`08:30`); kept as a
// helper anyway so a future tweak (e.g. drop seconds the server may emit)
// is one-place.
export const formatTimeCs = (hm: string | null | undefined): string => {
  if (!hm) return '';
  // Strip trailing `:ss` if the server sends `HH:mm:ss`.
  const stripped = hm.length > 5 ? hm.slice(0, 5) : hm;
  return stripped;
};

// ---------------------------------------------------------------------------
// Weekly-calendar helpers (CHR-03 §6 / ADR-0051). All operate on ISO
// LocalDate strings (`YYYY-MM-DD`) and reuse the file's noon-local Date
// construction to dodge DST edges (same rationale as `formatDateCs`). A
// week starts on Monday (ISO-8601 / Czech convention).
// ---------------------------------------------------------------------------

// Parse an ISO LocalDate into a noon-local Date, or `null` if malformed. The
// noon time avoids any DST midnight shift moving the wall date a day.
const parseIsoNoon = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const match = ISO_DATE_RE.exec(iso);
  if (!match) return null;
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  return new Date(y, mo - 1, d, 12, 0, 0);
};

// Monday of the week containing `iso` (ISO LocalDate). Falls back to
// `todayIsoDate()`'s Monday for malformed input so callers always get a
// usable week anchor. getDay(): 0 = Sunday … 6 = Saturday → Monday offset.
export const getWeekStart = (iso: string | null | undefined): string => {
  // `parseIsoNoon(todayIsoDate())` is always well-formed, so `dt` is non-null.
  const dt = parseIsoNoon(iso) ?? parseIsoNoon(todayIsoDate());
  if (!dt) return todayIsoDate();
  const day = dt.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day; // Sun → -6, else 1-day
  dt.setDate(dt.getDate() + mondayOffset);
  return isoFromParts(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

// The 7 ISO LocalDates Mon–Sun of the week containing `iso`. Crosses month /
// year boundaries correctly via Date arithmetic.
export const getWeekDays = (iso: string | null | undefined): string[] => {
  const base = parseIsoNoon(getWeekStart(iso));
  if (!base) return [];
  const days: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i, 12, 0, 0);
    days.push(isoFromParts(d.getFullYear(), d.getMonth(), d.getDate()));
  }
  return days;
};

// Add `n` weeks (may be negative) to an ISO LocalDate, returning the Monday of
// the resulting week. Used by the `< Dnes >` week navigation.
export const addWeeks = (iso: string | null | undefined, n: number): string => {
  const base = parseIsoNoon(getWeekStart(iso));
  if (!base) return getWeekStart(iso);
  const shifted = new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + n * 7,
    12,
    0,
    0,
  );
  return isoFromParts(shifted.getFullYear(), shifted.getMonth(), shifted.getDate());
};

// Format a week (given its Monday ISO date) as a Czech range label. Collapses
// shared month/year parts: `25.–31. 5. 2026` (same month), `29. 6. – 5. 7. 2026`
// (month boundary), `29. 12. 2025 – 4. 1. 2026` (year boundary). Returns empty
// string for malformed input (same guard contract as `formatDateCs`).
export const formatWeekRangeCs = (mondayIso: string | null | undefined): string => {
  const days = getWeekDays(mondayIso);
  const mon = parseIsoNoon(days[0]);
  const sun = parseIsoNoon(days[6]);
  if (!mon || !sun) return '';
  const sameYear = mon.getFullYear() === sun.getFullYear();
  const sameMonth = sameYear && mon.getMonth() === sun.getMonth();
  if (sameMonth) {
    // `25.–31. 5. 2026`
    return `${mon.getDate()}.–${sun.getDate()}. ${sun.getMonth() + 1}. ${sun.getFullYear()}`;
  }
  if (sameYear) {
    // `29. 6. – 5. 7. 2026`
    return (
      `${mon.getDate()}. ${mon.getMonth() + 1}. – ` +
      `${sun.getDate()}. ${sun.getMonth() + 1}. ${sun.getFullYear()}`
    );
  }
  // `29. 12. 2025 – 4. 1. 2026`
  return (
    `${mon.getDate()}. ${mon.getMonth() + 1}. ${mon.getFullYear()} – ` +
    `${sun.getDate()}. ${sun.getMonth() + 1}. ${sun.getFullYear()}`
  );
};
